import { Component, Fragment } from "react";
import { Route, Redirect } from "react-router-dom";
import { array, bool, func } from "prop-types";
import { Helmet } from "react-helmet";

import { routes, URL_ORIGIN } from "../../../constants";
import history from "../../../helpers/history";

import ErrorBoundary from "../../error-boundary";
import ErrorBoundaryFallbackApp from "../../error-boundary/components/ErrorBoundary.fallback-app";

/**
 * Wraps a route's component in an error boundary
 */
class AppRoute extends Component {
  constructor(props) {
    super(props);

    this.handleTrailingSlash = this.handleTrailingSlash.bind(this);
    this.handleRedirectMeta = this.handleRedirectMeta.bind(this);
    this.prerenderClear = this.prerenderClear.bind(this);
    this.prerenderReady = this.prerenderReady.bind(this);
    this.prerenderWait = this.prerenderWait.bind(this);
    this._prerenderTimer = null;

    this.state = {
      slashRedirect: null,
    };
  }

  /**
   * Enforce removal of trailing slash and default files from URLs for better SEO
   * @public
   */
  handleTrailingSlash() {
    const { authenticated, location, restricted } = this.props;

    if (!location || !history) {
      return;
    }

    let slashRedirect = null;

    if (
      (!restricted || (restricted && authenticated)) &&
      /.+\/$/.test(`${location.pathname}`)
    ) {
      slashRedirect = this.props.location.pathname.slice(0, -1);
    }

    if (/^\/(default|parts)\.html?$/.test(`${location.pathname}`)) {
      slashRedirect = routes.home.path;
    }

    if (/^\/contact(us)?\.html?$/.test(`${location.pathname}`)) {
      slashRedirect = routes.contact.path;
    }

    if (!!slashRedirect) {
      history.replace(slashRedirect);

      this.setState({
        slashRedirect: slashRedirect,
      });
    } else {
      this.handleRedirectMeta();
    }
  }

  /**
   * Display 301 meta tag if needed
   * @public
   */
  handleRedirectMeta() {
    const { location } = this.props;

    if (!!location.state && !!location.state.redirect) {
      // Re-using slashRedirect state to display meta tag
      this.setState({
        slashRedirect: location.state.redirect,
      });
    }
  }

  /**
   * Reset prerenderReady value and clear timer
   * @public
   */
  prerenderClear() {
    if (!!this._prerenderTimer) {
      clearTimeout(this._prerenderTimer);
    }

    window.prerenderReady = false;
  }

  /**
   * Set prerenderReady value for public routes
   * and clear timer
   * @public
   */
  prerenderReady() {
    if (!this.props.restricted && !window.prerenderReady) {
      window.prerenderReady = true;
    }

    if (!!this._prerenderTimer) {
      clearTimeout(this._prerenderTimer);
    }
  }

  /**
   * Fallback timer to set prerenderReady value
   * @public
   */
  prerenderWait() {
    this.prerenderClear();
    this._prerenderTimer = setTimeout(this.prerenderReady, 6000);
  }

  componentDidMount() {
    this.prerenderWait();
    this.handleTrailingSlash();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.path !== this.props.path) {
      this.prerenderWait();
      this.handleTrailingSlash();
    }
  }

  componentWillUnmount() {
    this.prerenderClear();
  }

  render() {
    const { prerenderReady } = this;
    const {
      authenticated,
      component: Component,
      restricted,
      restrictedToRoles,
      trackEvent,
      trackModalView,
      trackOutboundLink,
      updateAvailable,
      ...rest
    } = this.props;
    const { slashRedirect } = this.state;
    const allowed =
      !restricted ||
      (restricted && authenticated && !restrictedToRoles.length) ||
      (restricted &&
        authenticated &&
        !!restrictedToRoles.length &&
        restrictedToRoles.includes(this.props.userRole));

    return (
      <Route
        {...rest}
        render={props => (
          <ErrorBoundary
            location="app-route"
            fallback={ErrorBoundaryFallbackApp}
            prerenderReady={prerenderReady}
          >
            {allowed ? (
              <Fragment>
                {!!slashRedirect && (
                  <Helmet defer={false}>
                    <meta name="prerender-status-code" content="301" />
                    <meta
                      name="prerender-header"
                      content={`Location: ${URL_ORIGIN}${slashRedirect}`}
                    />
                  </Helmet>
                )}
                <Component
                  prerenderReady={prerenderReady}
                  trackEvent={trackEvent}
                  trackModalView={trackModalView}
                  trackOutboundLink={trackOutboundLink}
                  updateAvailable={updateAvailable}
                  {...props}
                />
              </Fragment>
            ) : (
              <Redirect
                to={{
                  pathname: routes.login.path,
                  state: { from: props.location },
                }}
              />
            )}
          </ErrorBoundary>
        )}
      />
    );
  }
}

AppRoute.propTypes = {
  authenticated: bool,
  restricted: bool,
  restrictedToRoles: array,
  trackEvent: func,
  trackModalView: func,
  trackOutboundLink: func,
};

AppRoute.defaultProps = {
  authenticated: false,
  restricted: false,
  restrictedToRoles: [],
};

export default AppRoute;
